import { NavigationStatus } from '@/enums/floor-guide';
import { useOrientation } from '@/hooks/useOrientation';
import type { FloorInfo, PoiContent } from '@/services/dto';
import { useConfigStore } from '@/stores';
import type { NearbyPoiItemType } from '@/types/map';
import { useCallback, useEffect, useMemo, useState } from 'react';

type Modal = {
    poiList: boolean;
    noPoiCategory: boolean;
    selectTransportationMode: boolean;
    navigationGuide: boolean;
    nearbyPoiList: boolean;
};

type Nearby = {
    nearbyPoiList: NearbyPoiItemType[];
    selectedPoiId?: string;
    title: string;
    building: string;
    floorName: string;
    type?: 'category' | 'show-location';
};

type State = {
    modal: Modal;
    markerIds: string[];
    nearestPoiFloor:  FloorInfo | null;
    isShowCategoryList: boolean;
    selectedPoiId?: string;
    selectedPoiContent?: PoiContent;
    isShowAllFloorGuide: boolean;
    navigationStatus: NavigationStatus;
    shouldReopenPoiList: boolean;
    nearby: Nearby;
};

const initialState: State = {
    modal: {
        poiList: false,
        noPoiCategory: false,
        selectTransportationMode: false,
        navigationGuide: false,
        nearbyPoiList: false,
    },
    markerIds: [],
    nearestPoiFloor: null,
    isShowCategoryList: true,
    isShowAllFloorGuide: false,
    navigationStatus: NavigationStatus.IDLE,
    shouldReopenPoiList: false,
    nearby: {
        nearbyPoiList: [],
        selectedPoiId: undefined,
        title: '',
        building: '',
        floorName: '',
        type: undefined,
    },
};

export const useFloorGuideState = () => {
    const [state, setState] = useState(structuredClone(initialState));
    const { isHorizontal } = useOrientation();
    const screenMode = useConfigStore((s) => s.config?.ui?.screenMode);
    const buildings = useConfigStore((state) => state?.config?.server?.store?.buildings);
    const language = useConfigStore((state) => state?.config?.ui?.language);

    const allPoiItems = useMemo(() => {
        if (!Array.isArray(buildings) || !buildings?.length) {
            return [];
        }
        return buildings.flatMap((item) => (Array.isArray(item?.poiContents) ? item?.poiContents : []));
    }, [buildings]);

    const handleSetModalState = useCallback((modal: Partial<Modal>) => {
        setState((prev) => ({
            ...prev,
            modal: {
                ...prev.modal,
                ...modal,
            },
        }));
    }, []);

    const handleSetState = useCallback((state: Partial<State>) => {
        setState((prev) => ({
            ...prev,
            ...state,
        }));
    }, []);

    const handleSetNearbyState = useCallback((nearby: Partial<Nearby>) => {
        setState((prev) => ({
            ...prev,
            nearby: {
                ...prev.nearby,
                ...nearby,
            },
        }));
    }, []);

    const handleResetNearbyState = useCallback(() => {
        setState((prev) => ({
            ...prev,
            nearby: structuredClone(initialState.nearby),
        }));
    }, []);

    const handleGetPoiContentById = useCallback(
        (poiId: string, contentId?: string) => {
            if (!Array.isArray(allPoiItems) || !allPoiItems?.length) {
                return undefined;
            }
            if (contentId) {
                const contentItem = allPoiItems.find((poi) => poi.id === contentId);
                if (contentItem) return contentItem;
            }
            return allPoiItems.find((poi) => poi.studioPoiId === poiId);
        },
        [allPoiItems]
    );

    const handleGetBuildingNameById = useCallback(
        (buildingId: string) => {
            if (!Array.isArray(buildings) || !buildings?.length) {
                return undefined;
            }

            const building = buildings.find(({ studioBuildingId }) => studioBuildingId === buildingId);
            return building?.name?.[language] || building?.name?.['ko'] || '';
        },
        [buildings, language]
    );

    useEffect(() => {
        setState(structuredClone(initialState));
    }, [screenMode]);

    useEffect(() => {
        setState(structuredClone(initialState));
    }, [isHorizontal]);

    useEffect(() => {
        return () => {
            setState(structuredClone(initialState));
        };
    }, []);

    return {
        state,
        modalState: state.modal,
        nearbyState: state.nearby,
        handleSetModalState,
        handleSetState,
        handleSetNearbyState,
        handleResetNearbyState,
        handleGetPoiContentById,
        handleGetBuildingNameById,
    };
};
